import ptBrLocale from 'date-fns/locale/pt-BR';
import enUsLocale from 'date-fns/locale/en-US';
import esLocale from 'date-fns/locale/es';
import { responseMessages } from './serverMessages';
import { yup } from 'src/utils/definitionsYup';
import { dashboard } from './dashboard';
import { step1, step2, step3, step4, step5 } from './onboarding';

import { enUS, esES, ptBR } from '@mui/x-date-pickers/locales';
import { profileDescriptionsLoc, profileDescriptionsLongLoc } from './profiles';
import { licenseDescriptions, licenses } from './licenses';
import { references } from './references';

export const locale = {
    en: enUsLocale,
    es: esLocale,
    pt: ptBrLocale,
}

export const dateLocale = {
    en: enUS,
    es: esES,
    pt: ptBR,
}

export const supportedLanguages = {
    en: 'English',
    es: 'Español',
    pt: 'Português',
};

const general = {
    en: {
        expandMenu: 'Expand',
        retractMenu: 'Show less',
        startNow: 'Start now',
        chooseProtectionProfile: 'Choose your protection profile',
        protectionProfile: 'Protection Profile',
        generateStrongPassword: 'Generate strong password',
        emailOrIdentifier: 'Email or identifier',
        never: 'never',
        optional: 'Optional',
        profileUpdateSuccessful: 'Profile Updated Successfully',
        langChangeLogoutRequired: 'If you changed the language, log out and back in for it to apply',
        profileUpdateFailed: 'Unable to update profile',
        loading: 'Loading...',
        password: 'Password',
        typeCurrentPword: 'Type current password',
        newPassword: 'New password',
        noDataAvailable: 'No Data Available',
        failedAttempts: 'Failed attempts',
        successfulAttempts: 'Successful attempts',
        theFollowingAdminsWill: 'The following users will be',
        theFollowingUsersWill: 'The following users will be',
        lessThanMinuteAgo: 'Less than a minute ago',
        hoursAnd: ' hours and ',
        hourAnd: ' hour and ',
        daysAgo: ' days ago',
        dayAgo: ' day ago',
        minutesAgo: ' minutes ago',
        minuteAgo: ' minute ago',
        osVersion: 'OS Version',
        appVersion: 'App Version',
        osID: 'OS',
        comingSoon: 'coming soon',
        support: 'Support',
    },
    pt: {
        expandMenu: 'Expand',
        retractMenu: 'Show less',
        startNow: "Vamos começar",
        chooseProtectionProfile: 'Escolha seu perfil de proteção',
        protectionProfile: 'Perfil de Proteção',
        generateStrongPassword: 'Gerar senha segura',
        emailOrIdentifier: 'Email or identificador',
        never: 'nunca',
        optional: 'Opcional',
        profileUpdateSuccessful: 'Atualização de perfil bem sucedida',
        langChangeLogoutRequired: 'Se alterou o idioma, faça logout e login novamente para aplicar',
        profileUpdateFailed: 'Não foi possível atualizar o perfil',
        loading: 'Carregando...',
        password: 'Senha',
        typeCurrentPword: 'Digite senha atual',
        newPassword: 'Nova senha',
        noDataAvailable: 'Dados inexistentes',
        failedAttempts: 'Tentativas mal-sucedidas',
        successfulAttempts: 'Tentativas bem sucedidas',
        theFollowingAdminsWill: 'Os seguintes usuários serão',
        theFollowingUsersWill: 'Os seguintes usuários serão',
        osID: 'SO',
        osVersion: 'Versão SO',
        appVersion: 'Versão App',
        minutesAgo: ' minutos atrás',
        minuteAgo: ' minuto atrás',
        daysAgo: ' dias atrás',
        dayAgo: ' dia atrás',
        hoursAnd: ' horas e ',
        hourAnd: ' hora e ',
        lessThanMinuteAgo: 'A menos de um minuto atrás',
        comingSoon: 'disponível em breve',
        support: 'Suporte',
    },
    es: {
        expandMenu: 'Expand',
        retractMenu: 'Show less',
        startNow: 'Start now',
        chooseProtectionProfile: 'Seleccione tu perfil de protección',
        protectionProfile: 'Perfil de Protección',
        generateStrongPassword: 'Generar contraseña segura',
        emailOrIdentifier: 'Email or identificador',
        never: 'nunca',
        optional: 'Opcional',
        profileUpdateSuccessful: 'Perfil actualizado con éxito',
        langChangeLogoutRequired: 'Si cambiaste el idioma, cierra sesión y vuelve a entrar para aplicarlo',
        profileUpdateFailed: 'No se pudo actualizar el perfil',
        loading: 'Cargando...',
        password: 'Contraseña',
        typeCurrentPword: 'Escriba la contraseña actual',
        newPassword: 'Nueva contraseña',
        noDataAvailable: 'No hay datos disponibles',
        failedAttempts: 'Intentos fallidos',
        successfulAttempts: 'Intentos exitosos',
        theFollowingAdminsWill: 'Los siguientes usuarios serán',
        theFollowingUsersWill: 'Los siguientes usuarios serán',
        osID: 'SO',
        osVersion: 'Versión SO',
        appVersion: 'Versión de la aplicación',
        minutesAgo: ' minutos atrás',
        minuteAgo: ' minuto atrás',
        daysAgo: ' días atrás',
        dayAgo: ' día atrás',
        hoursAnd: ' horas y ',
        hourAnd: ' hora y ',
        lessThanMinuteAgo: 'Hace menos de un minuto',
        comingSoon: 'próximamente',
        support: 'Soporte',
    }
}

const settings = {
    en: {
        generalSettings: 'General Settings',
        securitySettings: 'Security Settings',
        emailingEnabled: 'Use e-mail notifications',
        registrationEnabled: 'Allow outside users to register new companies',
        adminRegTokenExpiration: 'Token expiration (in minutes) for admin registrations',
        adminPwordResetTokenExpiration: 'Token expiration (in minutes) for admin password resets',
        otpLength: 'One-time password length',
        otpTTL: 'One-time password TTL (in minutes)',
    },
    pt: {
        generalSettings: 'Configurações Gerais',
        securitySettings: 'Configurações de Segurança',
        emailingEnabled: 'Usar notificações via e-mail',
        registrationEnabled: 'Permitir usuários externos criarem times',
        adminRegTokenExpiration: 'Expiração do código para registro de administradores (em minutos) ',
        adminPwordResetTokenExpiration: 'Expiração do código para redefinição de senhas de administradores (em minutos) ',
        otpLength: 'Comprimento do OTP',
        otpTTL: 'TTL do OTP (em minutos)',
    },
    es: {
        generalSettings: 'Configuraciones generales',
        securitySettings: 'Configuraciones de seguridad',
        emailingEnabled: 'Usar notificaciones por correo electrónico',
        registrationEnabled: 'Allow outside users to register new teams',
        adminRegTokenExpiration: 'Expiración del token (en minutos) para registros de administradores',
        adminPwordResetTokenExpiration: 'Expiración del token (en minutos) para restablecimientos de contraseña de administradores',
        otpLength: 'Longitud del OTP',
        otpTTL: 'TTL del OTP (en minutos)',
    }
}

const publicKeys = {
    en: {
        resendOtpCountdown: 'Request another one-time password in {seconds}s',
        resendOtpLink: 'Request another one-time password',
        confirmed: 'Your e-mail address has been confirmed successfully!',
        registerNow: 'Register now!',
        register: 'Register',
        toCreateAccount: 'Fill out the form below',
        readTermsOfService: 'I have read and agreed to the',
        termsOfService: 'terms of service',
        alreadyHaveAccount: 'Already have an account?',
        signIn: 'Sign In',
        signInPrompt: 'Enter your account e-mail below to sign in',
        requestOTP: 'Request one-time password',
        notYetHaveAccount: 'Do you not yet have an account?',
        backToSignIn: 'Go Back To Sign-In Page',
        email: 'Username (must be a valid e-mail address)',
        otpRequestSuccessful: 'One-time password request successfully sent. Check your inbox.'
    },
    pt: {
        resendOtpCountdown: 'Solicitar outro OTP em {seconds}s',
        resendOtpLink: 'Solicitar outro OTP',
        confirmed: 'Seu endereço e-mail foi confirmado com sucesso!',
        registerNow: 'Crie sua conta agora!',
        register: 'Registrar',
        toCreateAccount: 'Preencha o formulário abaixo',
        readTermsOfService: 'Eu li e concordo com os',
        termsOfService: 'termos de serviço',
        alreadyHaveAccount: 'Já possui uma conta?',
        signIn: 'Acessar',
        signInPrompt: 'Digite o seu e-mail para acessar',
        requestOTP: 'Solicitar senha de uso único',
        notYetHaveAccount: 'Você ainda não tem uma conta?',
        backToSignIn: 'Voltar a página de acesso',
        email: 'Nome do usuário (deve ser um e-mail válido)',
        otpRequestSuccessful: 'Solicitação de OTP enviada com sucesso. Verifique sua caixa de entrada.'
    },
    es: {
        resendOtpCountdown: 'Solicitar otra contraseña de un solo uso en {seconds}s',
        resendOtpLink: 'Solicitar otra contraseña de un solo uso',
        confirmed: '¡Tu dirección de correo electrónico ha sido confirmada con éxito!',
        registerNow: '¡Regístrate ahora!',
        register: 'Registrar',
        toCreateAccount: 'Complete el formulario abajo',
        readTermsOfService: 'He leído y acepto los',
        termsOfService: 'términos de servicio',
        alreadyHaveAccount: '¿Ya tienes una cuenta?',
        signIn: 'Iniciar sesión',
        signInPrompt: 'Ingresa tu correo electrónico para iniciar sesión',
        requestOTP: 'Solicitar contraseña de un solo uso',
        notYetHaveAccount: '¿Aún no tienes una cuenta?',
        backToSignIn: 'Volver a la página de inicio de sesión',
        email: 'Nombre de usuario (debe ser una dirección de correo electrónico válida)',
        otpRequestSuccessful: 'Solicitud de contraseña de un solo uso enviada con éxito. Verifique su bandeja de entrada.'
    }
}

const actionsStatus = {
    en: {
        toggleMenu: 'Toggle side menu',
        confirm: 'Confirm',
        or: 'or',
        paginationRowsPerPage: 'Items per page',
        paginationOf: 'of',
        na: 'Not Available',
        save: 'Save',
        upload: 'Upload',
        close: 'Close',
        back: 'Back',
        next: 'Next',
        edit: 'Edit',
        signOut: 'Sign out',
        active: 'Active',
        pending: 'Pending',
        actions: 'Actions',
        error: 'Error',
        search: 'Search',
        delete: 'Delete',
        deleted: 'deleted',
        block: 'Block',
        blocked: 'blocked',
        blocked2: 'Blocked',
        activate: 'Activate',
        activated: 'activated',
        terminate: 'Terminate',
        terminateAcctHeader: 'Terminate Account',
        terminateAcctWarning: 'This action is irreversible. Your account and all associated data will be permanently deleted.',
        viewEdit: 'View/Edit',
        manage: 'Manage',
        expired: 'expired',
        backAdminManagement: '< Back to User Management',
        create: 'Create',
    },
    pt: {
        toggleMenu: 'Ativar menu lateral',
        confirm: 'Confirmar',
        or: 'ou',
        paginationRowsPerPage: 'Itens por página',
        paginationOf: 'de',
        na: 'Indisponível',
        save: 'Salvar',
        upload: 'Carregar',
        close: 'Fechar',
        back: 'Voltar',
        next: 'Próxima',
        edit: 'Editar',
        signOut: 'Sair',
        active: 'Ativo',
        pending: 'Pendente',
        actions: 'Ações',
        error: 'Erro',
        search: 'Busca',
        delete: 'Remover',
        deleted: 'removidos',
        block: 'Bloquear',
        blocked: 'bloqueados',
        blocked2: 'Bloqueado',
        activate: 'Ativar',
        activated: 'ativados',
        terminate: 'Encerrar Conta',
        terminateAcctHeader: 'Encerrar Conta',
        terminateAcctWarning: 'Esta ação é irreversível. Sua conta e todos os dados associados serão excluídos permanentemente.',
        viewEdit: 'Visualizar/Editar',
        manage: 'Gerenciar',
        expired: 'expiradas',
        backAdminManagement: '< Voltar ao Painel de usuários',
        create: 'Criar',
    },
    es: {
        toggleMenu: 'Activar menú lateral',
        confirm: 'Confirmar',
        or: 'o',
        paginationRowsPerPage: 'Elementos por página',
        paginationOf: 'de',
        na: 'No disponible',
        save: 'Guardar',
        upload: 'Subir',
        close: 'Cerrar',
        back: 'Atrás',
        next: 'Siguiente',
        edit: 'Editar',
        signOut: 'Cerrar sesión',
        active: 'Activo',
        pending: 'Pendiente',
        actions: 'Acciones',
        error: 'Error',
        search: 'Buscar',
        delete: 'Eliminar',
        deleted: 'eliminado',
        block: 'Bloquear',
        blocked: 'bloqueado',
        blocked2: 'Bloqueado',
        activate: 'Activar',
        activated: 'activado',
        terminate: 'Terminar',
        terminateAcctHeader: 'Terminar Cuenta',
        terminateAcctWarning: 'Esta acción es irreversible. Su cuenta y todos los datos asociados serán eliminados permanentemente.',
        viewEdit: 'Ver/Editar',
        manage: 'Gestionar',
        expired: 'expirado',
        backAdminManagement: '< Volver a la gestión de usuarios',
        create: 'Crear'
    }
}


const headers = {
    en: {
        quickAction: 'Quick Action',
        settings: 'Settings',
        general: 'General',
        dashboard: 'Dashboard',
        accessLogs: 'Access Logs',
        greeting: 'Welcome to SecurePath AI',
        purchaseYourLicense: 'Purchase your license',
    },
    pt: {
        quickAction: 'Ação Rápida',
        settings: 'Configurações',
        general: 'Geral',
        dashboard: 'Dashboard',
        accessLogs: 'Registros de Acesso',
        greeting: 'Bem vindo a SecurePath AI',
        teams: 'Times',
        team: 'Time',
        teamList: 'Lista de times',
        purchaseYourLicense: 'Adquira sua licença',
    },
    es: {
        quickAction: 'Acción Rápida',
        settings: 'Configuraciones',
        general: 'General',
        dashboard: 'Dashboard',
        accessLogs: 'Registros de acceso',
        greeting: 'Bienvenido a SecurePath AI',
        purchaseYourLicense: 'Purchase your license',
    }
}

const appSettings = {
    en: {
        apps: 'Applications',
        storeURL: 'URL Download Path',
    },
    pt: {
        apps: 'Aplicativos',
        storeURL: 'URL Para Baixar',
    },
    es: {
        apps: 'Aplicaciones',
        storeURL: 'Ruta de descarga de URL',
    }
}

const passwordReset = {
    en: {
    },
    pt: {
    },
    es: {
    }
}

const profileInfo = {
    en: {
        city: 'City',
        institution: 'Institution',
        profile: 'Profile',
        profileFor: 'Profile for',
        profileDetails: 'Profile Details',
        myProfile: 'My Profile',
        expiration: 'Expiration',
        lastSeen: 'Last seen online',
        ipAddr: 'IP Address',
        name: 'Name',
        description: 'Description',
        firstName: 'First Name',
        lastName: 'Last Name',
        username: 'Username',
        language: 'Language',
        country: 'Country',
        notes: 'Notes',
        timezone: 'Time zone',
        location: 'Location',
        dateTime: 'Date/Time',
        role: 'Role',
        access: 'Access level',
    },
    pt: {
        city: 'Cidade',
        institution: 'Instituição',
        profile: 'Perfil',
        profileFor: 'Perfil de',
        profileDetails: 'Detalhes do Perfil',
        myProfile: 'Meu Perfil',
        expiration: 'Expiração',
        lastSeen: 'Último acesso',
        ipAddr: 'Endereço IP',
        name: 'Nome',
        description: 'Descrição',
        firstName: 'Nome',
        lastName: 'Sobrenome',
        username: 'Usuário',
        language: 'Idioma',
        country: 'País',
        notes: 'Observações',
        timezone: 'Fuso-horário',
        location: 'Localização',
        dateTime: 'Data/Hora',
        role: 'Papel',
        access: 'Nível de acesso',
    },
    es: {
        city: 'Ciudad',
        institution: 'Institución',
        profile: 'Perfil',
        profileFor: 'Perfil para',
        profileDetails: 'Detalles del perfil',
        myProfile: 'Mi perfil',
        expiration: 'Expiración',
        lastSeen: 'Visto por última vez',
        ipAddr: 'Dirección IP',
        name: 'Nombre',
        description: 'Descripción',
        firstName: 'Nombre de pila',
        lastName: 'Apellido',
        username: 'Nombre de usuario',
        language: 'Idioma',
        country: 'País',
        notes: 'Notas',
        timezone: 'Zona horaria',
        location: 'Ubicación',
        dateTime: 'Fecha/Hora',
        role: 'Rol',
        access: 'Nivel de acceso',
    }
}

const admins = {
    en: {
        testToken: 'Validate Token',
        superAdmin: 'Super User',
        users: 'Users',
        licenses: 'Licenses',
        admin: 'User',
        adminList: 'User List',
        adminProfile: 'User Profile',
        manageAdmins: 'Manage Users',
        createAdmin: 'Create User',
        createAdminFill: 'Please fill in information below',
        createAdminSuccessful: 'User Created Successfully!',
        createAdminInfo: 'User information',
        createAdminAsSuperAdmin: 'Create as Super-User?',
        createAdminRegistrationMethod: 'Registration Method',
        createAdminTooltip: `
        <b>Define password</b> - the user will be automatically activated.<br /> <br />
        <b>Send invite via e-mail (when emailing is enabled)</b> - the created admin will have to manually
        activate the account by clicking on the link sent to the chosen e-mail address.`,
        createAdminSendInvite: ' Send Invite via Email',
        createAdminDefinePword: 'Define Password',
        teams: 'Companies',
        team: 'Company',
        teamList: 'Company List',
    },
    pt: {
        testToken: 'Validar Token',
        superAdmin: 'Super Usuário',
        users: 'Administradores',
        licenses: 'Licenças',
        admin: 'Usuário',
        adminList: 'Lista de Usuários',
        adminProfile: 'Perfil do Usuário',
        manageAdmins: 'Gerenciar Usuários',
        createAdmin: 'Criar Usuário',
        createAdminFill: 'Por favor preencha a informação abaixo',
        createAdminSuccessful: 'Usuário criado com sucesso!',
        createAdminInfo: 'Informação do usuário',
        createAdminAsSuperAdmin: 'Criar como super-usuário?',
        createAdminRegistrationMethod: 'Método de registro',
        createAdminTooltip: `
        <b>Definir senha</b> - o usuário será ativado automaticamente.<br /> <br />
        <b>Enviar convite via e-mail (quando e-mails estão ativados)</b> - o administrador deverá manualmente ativar sua conta
        clicando no link enviado para o e-mail escolhido.`,
        createAdminSendInvite: 'Enviar convite via e-mail',
        createAdminDefinePword: 'Definir senha',
    },
    es: {
        testToken: 'Validar Token',
        superAdmin: 'Super Usuario',
        users: 'Usuarios',
        licenses: 'Licencias',
        admin: 'Usuario',
        adminList: 'Lista de Usuarios',
        adminProfile: 'Perfil del Usuario',
        manageAdmins: 'Gestionar Usuarios',
        createAdmin: 'Crear usuario',
        createAdminFill: 'Por favor complete la información a continuación',
        createAdminSuccessful: '¡Usuario creado con éxito!',
        createAdminInfo: 'Información del usuario',
        createAdminAsSuperAdmin: '¿Crear como Super-Usuario?',
        createAdminRegistrationMethod: 'Método de registro',
        createAdminTooltip: `
            <b>Definir contraseña</b> - el usuario será activado automáticamente.<br /> <br />
            <b>Enviar invitación por correo electrónico (cuando se habilitan los correos electrónicos)</b> - el administrador creado deberá activar manualmente la cuenta haciendo clic en el enlace enviado a la dirección de correo electrónico elegida.`,
        createAdminSendInvite: 'Enviar invitación por correo electrónico',
        createAdminDefinePword: 'Definir contraseña',
        teams: 'Equipos',
        team: 'Equipo',
        teamList: 'Lista de equipos',
    }
}

const submodulesNav = {
    en: {
        nextModule: 'Next Module',
        progress: "Progress",
        error: "Error",
        failedToLoad: "Failed to load module content",
        slideCounter: "Slide {current} of {total}",
        stepCounter: "Step {current} of {total}",
        markAsCompleted: "Mark as completed",
        previous: "Previous",
        next: "Next",
        resume: 'Resume'
    },
    pt: {
        nextModule: 'Próximo Módulo',
        progress: "Progresso",
        error: "Erro",
        failedToLoad: "Falha ao carregar o conteúdo do módulo",
        slideCounter: "Slide {current} de {total}",
        stepCounter: "Passo {current} de {total}",
        markAsCompleted: "Marcar como concluído",
        previous: "Anterior",
        next: "Próximo",
        resume: 'Retomar'
    },
    es: {
        nextModule: 'Siguiente módulo',
        progress: "Progreso",
        error: "Error",
        failedToLoad: "Error al cargar el contenido del módulo",
        slideCounter: "Diapositiva {current} de {total}",
        stepCounter: "Paso {current} de {total}",
        markAsCompleted: "Marcar como completado",
        previous: "Anterior",
        next: "Siguiente",
        resume: 'Reanudar'
    }
};

export const messages = {
    en: {
        ...licenseDescriptions.en,
        ...profileDescriptionsLoc.en,
        ...profileDescriptionsLongLoc.en,
        ...submodulesNav.en,
        ...dashboard.en,
        ...step1.en,
        ...step2.en,
        ...step3.en,
        ...step4.en,
        ...step5.en,
        ...responseMessages.en,
        ...yup.en,
        ...general.en,
        ...settings.en,
        ...publicKeys.en,
        ...headers.en,
        ...actionsStatus.en,
        ...licenses.en,
        ...appSettings.en,
        ...passwordReset.en,
        ...profileInfo.en,
        ...admins.en,
        ...references.en,
    },
    pt: {
        ...licenseDescriptions.pt,
        ...profileDescriptionsLoc.pt,
        ...profileDescriptionsLongLoc.pt,
        ...submodulesNav.pt,
        ...dashboard.pt,
        ...step1.pt,
        ...step2.pt,
        ...step3.pt,
        ...step4.pt,
        ...step5.pt,
        ...responseMessages.pt,
        ...yup.pt,
        ...general.pt,
        ...settings.pt,
        ...publicKeys.pt,
        ...headers.pt,
        ...actionsStatus.pt,
        ...licenses.pt,
        ...appSettings.pt,
        ...passwordReset.pt,
        ...profileInfo.pt,
        ...admins.pt,
        ...references.pt,
    },
    es: {
        ...licenseDescriptions.es,
        ...profileDescriptionsLoc.es,
        ...profileDescriptionsLongLoc.es,
        ...submodulesNav.es,
        ...dashboard.es,
        ...step1.es,
        ...step2.es,
        ...step3.es,
        ...step4.es,
        ...step5.es,
        ...responseMessages.es,
        ...yup.es,
        ...general.es,
        ...settings.es,
        ...publicKeys.es,
        ...headers.es,
        ...actionsStatus.es,
        ...licenses.es,
        ...appSettings.es,
        ...passwordReset.es,
        ...profileInfo.es,
        ...admins.es,
        ...references.es,
    }
};